import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * ARMOURY (Оружейная / smithy) — the martial heart of Castle Hearthhold: a busy working smithy where the
 * guild's gear is forged and the trophy steel is kept. The HERO + room's brightest beat is the FORGE in
 * the NE quadrant, its coals glowing forge-orange, the ANVIL squat beside it ringing under the hammer.
 * A work-bench (small table + stool) sits dead-centre by the bench; the south + west walls bristle with a
 * trophy line of heraldic SHIELDS, two WEAPON RACKS, a polished SUIT OF ARMOUR sentinel and an arrow
 * BARREL. Wall torches on the flanks keep the steel warm-lit. Honeyed, busy, martial — never grim.
 *
 * Composition note: the FORGE is the room's single brightest light-beat — supporting fill is ONE west
 * wall torch only (the east flank is left to the forge's own glow), so the hero is never out-lit. The
 * rest is on-purpose martial set-dressing riding the walls, with the central cross + door mouths clear.
 *
 * Geometry: zone (360,360) 440×400 → centre (580,560). Doors at N@x580, W@y560, E@y560, S@x580 — the
 * central cross + each door mouth are left clear; set-dressing rides the walls + the NE forge quadrant.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor: a worn runner laid first, drawing the eye from the bench toward the forge (NE). ──
  place(ctx, kit.runnerRug(art, 150, 90), 640, 490);

  // ── HERO: the FORGE in the NE quadrant — the room's brightest forge-orange pool of light. ──
  place(ctx, kit.forge(art), 700, 460);
  // ── FIRE-MATCH partner: the ANVIL squat beside the forge, in reach of its coals. ──
  place(ctx, kit.anvil(art), 640, 520);

  // ── The central WORK-BENCH: a small bench table with a sit-able stool (SIT-MATCH: armoury-stool). ──
  place(ctx, kit.smallTable(art, 30), 580, 560);
  place(ctx, kit.woodStool(art), 580, 560);

  // ── Trophy line of heraldic SHIELDS on the north wall, colours varied (the guild's standing gear). ──
  place(ctx, kit.shield(art, 0x8a2f2f), 500, 400); // crimson
  place(ctx, kit.shield(art, 0x2f4a8a), 560, 400); // azure
  place(ctx, kit.shield(art, 0x2f7a4a), 620, 400); // vert

  // ── WEAPON RACKS on the walls (NW + SE), bristling with the smith's output. ──
  place(ctx, kit.weaponRack(art), 420, 420);
  place(ctx, kit.weaponRack(art), 760, 650);

  // ── A polished SUIT OF ARMOUR sentinel guarding the SW corner. ──
  place(ctx, kit.suitOfArmour(art), 420, 680);

  // ── A BARREL of arrows in the SE, fletched stock by the rack. ──
  place(ctx, kit.barrel(art), 740, 680);

  // ── A single WEST WALL TORCH for fill — the east flank is left to the forge's own glow so the
  //    hero forge stays the room's one brightest light-beat (no competing pool beside it). ──
  place(ctx, kit.wallTorch(art), 400, 560);
};

export default decorate;
